import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { TRANSLATIONS, LANGUAGE_OPTIONS } from "./i18nCatalog";

const STORAGE_KEY = "polla_lang";

function detectDefaultLang() {
  if (typeof navigator === "undefined") return "es";
  const langs = (navigator.languages || [navigator.language || "es"]).map((l) => l.toLowerCase().split("-")[0]);
  for (const l of langs) {
    if (l === "en") return "en";
    if (l === "es") return "es";
  }
  return "es";
}

function interpolate(str, vars = {}) {
  return str.replace(/\{(\w+)\}/g, (_, k) => (k in vars ? String(vars[k]) : `{${k}}`));
}

const I18nContext = createContext({
  lang: "es",
  setLang: () => {},
  t: (k) => k,
  langLabel: "Español",
});

export function I18nProvider({ children }) {
  const [lang, setLangState] = useState(() => {
    if (typeof window === "undefined") return "es";
    return localStorage.getItem(STORAGE_KEY) || detectDefaultLang();
  });

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, lang);
      document.documentElement.setAttribute("lang", lang);
    } catch {
      // Private browsing or storage quota: keep lang in memory only.
    }
  }, [lang]);

  const t = useMemo(() => {
    const dict = TRANSLATIONS[lang] || TRANSLATIONS.es;
    const fallback = TRANSLATIONS.es;
    return (key, vars) => {
      // pluralization: key + "_one" / "_other"
      if (vars && typeof vars.count === "number") {
        const k = `${key}_${vars.count === 1 ? "one" : "other"}`;
        const val = dict[k] || fallback[k];
        if (val) return interpolate(val, vars);
      }
      const val = dict[key] ?? fallback[key] ?? key;
      return interpolate(val, vars || {});
    };
  }, [lang]);

  const langLabel = useMemo(
    () => LANGUAGE_OPTIONS.find((o) => o.id === lang)?.label || lang,
    [lang]
  );

  // Stable context value so children consumers don't re-render on every parent update.
  const value = useMemo(
    () => ({ lang, setLang: setLangState, t, langLabel }),
    [lang, t, langLabel]
  );

  return (
    <I18nContext.Provider value={value}>
      {children}
    </I18nContext.Provider>
  );
}

export function useI18n() {
  return useContext(I18nContext);
}

export { LANGUAGE_OPTIONS };
